import { memo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { formatCurrency } from "@/shared/lib/formatters";
import type { InvoiceResponseDTO } from "../types/invoice.types";

interface InvoiceDeleteDialogProps {
  open: boolean;
  loading: boolean;
  invoice: InvoiceResponseDTO | null;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Dialog de confirmação de exclusão definitiva de uma nota fiscal.
 *
 * Exibe ID, externalId e valor para evitar exclusão acidental.
 *
 * @example
 * <InvoiceDeleteDialog
 *   open={dialogMode === 'delete'}
 *   loading={dialogLoading}
 *   invoice={selectedInvoice}
 *   onClose={closeDialog}
 *   onConfirm={handleDeleteConfirm}
 * />
 */
export const InvoiceDeleteDialog = memo(function InvoiceDeleteDialog({
  open,
  loading,
  invoice,
  onClose,
  onConfirm,
}: InvoiceDeleteDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Excluir Nota Fiscal</DialogTitle>
          <DialogDescription>
            Tem certeza que deseja excluir esta nota fiscal?
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="p-4 bg-destructive/10 rounded-md border border-destructive/20">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Nota Fiscal #{invoice?.id}</p>
                {invoice?.externalId ? (
                  <p className="text-sm text-muted-foreground">
                    ID Externo: {invoice.externalId}
                  </p>
                ) : null}
                {invoice ? (
                  <p className="text-sm text-muted-foreground">
                    Valor: {formatCurrency(invoice.value)}
                  </p>
                ) : null}
                <p className="text-sm text-destructive mt-2">
                  Esta ação não pode ser desfeita!
                </p>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading}>
            {loading ? "Excluindo..." : "Excluir Nota"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
